/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { RefObject, useLayoutEffect, useMemo, useRef, useState } from "react";

interface ElementSizeHook<T> {
  ref: RefObject<T>;
  width: number;
  height: number;
  x: number;
  y: number;
  yRelative: number;
}

const INITIAL_ELEMENT_SIZE = {
  width: 0,
  height: 0,
  x: 0,
  y: 0,
  yRelative: 0,
};

interface UseElementSizeProps {
  disabled?: boolean;
}

export const useElementSize = <T extends HTMLElement>(
  props?: UseElementSizeProps
): ElementSizeHook<T> => {
  const disabled = props?.disabled ?? false;
  const ref = useRef<T>(null);

  const [dims, setDimensions] = useState(INITIAL_ELEMENT_SIZE);
  const animationFrameRef = useRef<number>(0);

  useLayoutEffect(() => {
    const elem = ref.current;
    if (!elem || disabled) {
      return;
    }

    const observer = new ResizeObserver((resizeEntries) => {
      animationFrameRef.current = requestAnimationFrame(() => {
        const rect = elem.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
          x: rect.x,
          y: rect.y,
          yRelative:
            rect.y - document.documentElement.getBoundingClientRect().top,
        });
      });
    });

    observer.observe(elem);

    return (): void => {
      cancelAnimationFrame(animationFrameRef.current);
      observer.unobserve(elem);
    };
  }, [disabled]);

  return useMemo(() => {
    if (disabled) {
      return { ref, ...INITIAL_ELEMENT_SIZE };
    } else {
      return {
        ref,
        ...dims,
      };
    }
  }, [dims, disabled]);
};
